import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, Upload, FileText, Image, Trash2, Check, Volume2, VolumeX, Car, Bus, Plane, Loader2, User, Calendar, Phone, Mail, Camera, FileCheck, CreditCard, Send } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Step Icon Component (disabled)
const AnimatedStepIcon = () => null;

// Voice messages for each step in different languages
const VOICE_MESSAGES = {
  en: {
    visaDuration: "Please select your visa duration: 30 days or 60 days.",
    step1: "Please enter your first name and last name as they appear on your passport.",
    step2: "Please select your planned travel date.",
    step3: "Please enter your phone number or Telegram number so we can share documents to your Telegram once approved.",
    step4: "Please enter your email address for booking confirmation.",
    step5: "Please upload a clear photo of your passport.",
    step6: "Please upload a personal photo for the visa application.",
    step7: "Please review and confirm your details.",
    debitCardStep: "Would you like to add a UAE bank debit card to your freelance visa? It costs an additional 500 AED.",
    visaChangeTransport: "How do you want to do your visa change? Please choose: by car, by bus, or by airplane.",
    familyStep0: "Who do you want to apply the Family Visa for? Please select: Husband, Son, Daughter, or Mother.",
    familyStep1: "Please upload the passport and a personal photo of your family member.",
    familyStep2: "Here is the Emirates ID preview with the information from the passport. Please check the details.",
    familyStep3: "Please enter your phone number or Telegram, and your email address, so we can share the documents with you.",
    familyStep4: "Please review all the details and confirm your Family Visa application."
  },
  ru: {
    visaDuration: "Пожалуйста, выберите срок визы: 30 дней или 60 дней.",
    step1: "Пожалуйста, введите ваше имя и фамилию, как указано в паспорте.",
    step2: "Пожалуйста, выберите планируемую дату поездки.",
    step3: "Пожалуйста, введите номер телефона или Telegram, чтобы мы могли отправить документы в Telegram после одобрения.",
    step4: "Пожалуйста, введите ваш адрес электронной почты.",
    step5: "Пожалуйста, загрузите четкую фотографию вашего паспорта.",
    step6: "Загрузите чёткое личное фото для визовой заявки.",
    step7: "Пожалуйста, проверьте и подтвердите ваши данные.",
    debitCardStep: "Хотите добавить дебетовую карту ОАЭ к фриланс визе? Дополнительная стоимость — 500 дирхамов.",
    visaChangeTransport: "Как вы хотите сделать визаран? Выберите: на машине, на автобусе или на самолёте.",
    familyStep0: "Для кого вы хотите оформить семейную визу? Выберите: муж, сын, дочь или мать.",
    familyStep1: "Пожалуйста, загрузите паспорт и личную фотографию члена семьи.",
    familyStep2: "Вот предварительный просмотр Emirates ID с информацией из паспорта. Проверьте данные.",
    familyStep3: "Пожалуйста, введите номер телефона или Telegram и адрес электронной почты, чтобы мы могли поделиться документами.",
    familyStep4: "Пожалуйста, проверьте все данные и подтвердите заявку на семейную визу."
  },
  uz: {
    visaDuration: "Iltimos, viza muddatini tanlang: 30 kun yoki 60 kun.",
    step1: "Iltimos, pasportingizdagi ismingiz va familiyangizni kiriting.",
    step2: "Iltimos, rejalashtirilgan sayohat sanasini tanlang.",
    step3: "Iltimos, telefon raqamingiz yoki Telegram raqamingizni kiriting — tasdiqlangandan so'ng hujjatlarni Telegram orqali yuboramiz.",
    step4: "Iltimos, elektron pochta manzilingizni kiriting.",
    step5: "Iltimos, pasportingizning aniq rasmini yuklang.",
    step6: "Iltimos, shaxsiy rasmingizni yuklang.",
    step7: "Iltimos, ma'lumotlaringizni tekshiring va tasdiqlang.",
    debitCardStep: "Frilanser vizangizga BAA bank debet kartasini qo'shmoqchimisiz? Qo'shimcha narx — 500 AED.",
    visaChangeTransport: "Viza almashtirishni qanday qilmoqchisiz? Tanlang: mashina, avtobus yoki samolyot.",
    familyStep0: "Oilaviy viza kimga kerak? Tanlang: er, o'g'il, qiz yoki ona.",
    familyStep1: "Iltimos, oila a'zosining pasporti va shaxsiy rasmini yuklang.",
    familyStep2: "Mana pasportdagi ma'lumotlar bilan Emirates ID ko'rinishi. Ma'lumotlarni tekshiring.",
    familyStep3: "Iltimos, telefon raqamingiz yoki Telegram raqamingizni va elektron pochtangizni kiriting — hujjatlarni siz bilan ulashamiz.",
    familyStep4: "Iltimos, barcha ma'lumotlarni tekshiring va oilaviy viza arizasini tasdiqlang."
  }
};

const NATIONALITIES = [
  "Afghan", "Albanian", "Algerian", "American", "Argentinian", "Armenian", "Australian", "Austrian", "Azerbaijani",
  "Bahraini", "Bangladeshi", "Belarusian", "Belgian", "Bolivian", "Bosnian", "Brazilian", "British", "Bulgarian",
  "Cambodian", "Cameroonian", "Canadian", "Chilean", "Chinese", "Colombian", "Croatian", "Cuban", "Czech",
  "Danish", "Dominican", "Dutch",
  "Ecuadorian", "Egyptian", "Emirati", "Estonian", "Ethiopian",
  "Filipino", "Finnish", "French",
  "Georgian", "German", "Ghanaian", "Greek", "Guatemalan",
  "Haitian", "Hungarian",
  "Indian", "Indonesian", "Iranian", "Iraqi", "Irish", "Israeli", "Italian",
  "Jamaican", "Japanese", "Jordanian",
  "Kazakh", "Kenyan", "Korean", "Kuwaiti", "Kyrgyz",
  "Latvian", "Lebanese", "Libyan", "Lithuanian", "Luxembourgish",
  "Malaysian", "Mexican", "Moldovan", "Mongolian", "Moroccan",
  "Nepalese", "New Zealander", "Nigerian", "Norwegian",
  "Omani",
  "Pakistani", "Palestinian", "Panamanian", "Paraguayan", "Peruvian", "Polish", "Portuguese",
  "Qatari",
  "Romanian", "Russian", "Rwandan",
  "Saudi", "Senegalese", "Serbian", "Singaporean", "Slovak", "Slovenian", "Somali", "South African", "Spanish", "Sri Lankan", "Sudanese", "Swedish", "Swiss", "Syrian",
  "Tajik", "Tanzanian", "Thai", "Tunisian", "Turkish", "Turkmen",
  "Ukrainian", "Uruguayan", "Uzbek",
  "Venezuelan", "Vietnamese",
  "Yemeni",
  "Zambian", "Zimbabwean"
];

const StepByStepBookingModal = ({ onClose, selectedService }) => {
  const { user, token } = useAuth();
  const { t, language } = useLanguage();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const currentLanguage = language || 'en';
  
  // Check if this is a Dubai Visa service (needs duration selection)
  const isDubaiVisa = selectedService?.name?.toLowerCase().includes('dubai') || 
                      selectedService?.name?.toLowerCase().includes('visa') && 
                      !selectedService?.name?.toLowerCase().includes('change') &&
                      !selectedService?.name?.toLowerCase().includes('freelance') &&
                      !selectedService?.name?.toLowerCase().includes('family');
  
  const isVisaChange = selectedService?.name?.toLowerCase().includes('change');
  const isFamilyVisa = selectedService?.name?.toLowerCase().includes('family');
  const isFreelance = selectedService?.name?.toLowerCase().includes('freelance');
  
  const [step, setStep] = useState(isFamilyVisa ? 0 : isDubaiVisa ? 0 : isVisaChange ? -1 : 1);
  const [visaDuration, setVisaDuration] = useState(null); // '30' or '60'
  const [transportMethod, setTransportMethod] = useState(null); // 'car', 'bus', 'airplane'
  const [familyMember, setFamilyMember] = useState(null); // 'husband', 'son', 'daughter', 'mother'
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceError, setVoiceError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [wantsDebitCard, setWantsDebitCard] = useState(null); // null = not chosen, true/false
  
  const totalSteps = isFamilyVisa ? 5 : isDubaiVisa ? 8 : isVisaChange ? 9 : isFreelance ? 8 : 7;
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfPlanning: '',
    phone: user?.mobile || '',
    telegram: '',
    sameAsPhone: false, // default OFF — show both fields so client can fill them; can check to reuse phone
    email: user?.email || ''
  });
  
  const [passportFile, setPassportFile] = useState(null);
  const [passportPreview, setPassportPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  
  const passportInputRef = useRef(null);
  const photoInputRef = useRef(null);
  
  // OCR state
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrData, setOcrData] = useState({ nationality: '', date_of_birth: '', passport_number: '', issue_date: '', expiry_date: '', first_name: '', last_name: '', given_names: '', surname: '' });
  const [ocrDone, setOcrDone] = useState(false);
  const [ocrError, setOcrError] = useState('');
  
  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.classList.add('modal-open');
    document.body.style.top = `-${scrollY}px`;
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.top = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  const TRANSPORT_PRICES = { car: 500, bus: 250, airplane: 700 };

  // Get the displayed price based on visa duration selection
  const getDisplayPrice = () => {
    if (isDubaiVisa && visaDuration) {
      return visaDuration === '30' ? 'AED 500' : 'AED 800';
    }
    if (isVisaChange) {
      let total = 0;
      if (visaDuration) total += visaDuration === '30' ? 500 : 800;
      if (transportMethod) total += TRANSPORT_PRICES[transportMethod];
      return total > 0 ? `AED ${total}` : '';
    }
    if (isFreelance && wantsDebitCard) {
      const basePrice = selectedService?.price || '';
      const numericPrice = parseInt(basePrice.replace(/[^0-9]/g, '')) || 0;
      return `AED ${(numericPrice + 500).toLocaleString()}`;
    }
    return selectedService?.price || '';
  };

  const getDisplayServiceName = () => {
    if (isDubaiVisa && visaDuration) {
      return `${t('dubaiVisa') || 'Dubai Visa'} ${visaDuration} ${t('days') || 'Days'}`;
    }
    if (isVisaChange && visaDuration) {
      const methodLabel = transportMethod ? ` (${t('by') || 'by'} ${t(transportMethod) || transportMethod})` : '';
      return `${t('visaChange') || 'Visa Change'} ${visaDuration} ${t('days') || 'Days'}${methodLabel}`;
    }
    if (isFamilyVisa && familyMember) {
      return `${t('familyVisa') || 'Family Visa'} - ${t(familyMember) || familyMember}`;
    }
    return selectedService?.name || '';
  };
  
  // Load available voices
  const audioRef = useRef(null);
  
  const getVoiceMessage = useCallback((key) => {
    return VOICE_MESSAGES[currentLanguage]?.[key] || VOICE_MESSAGES.en[key];
  }, [currentLanguage]);
  
  const speak = useCallback(async (text) => {
    if (!voiceEnabled || !text) return;
    
    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    
    try {
      setIsSpeaking(true);
      const response = await fetch(`${BACKEND_URL}/api/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, lang: currentLanguage })
      });
      
      if (!response.ok) throw new Error('TTS failed');
      
      const data = await response.json();
      const audioSrc = `data:audio/mp3;base64,${data.audio}`;
      const audio = new Audio(audioSrc);
      audioRef.current = audio;
      
      audio.onended = () => setIsSpeaking(false);
      audio.onerror = () => setIsSpeaking(false);
      await audio.play();
    } catch (e) {
      console.error('TTS error:', e);
      setIsSpeaking(false);
      setVoiceError(true);
      setTimeout(() => setVoiceError(false), 5000);
    }
  }, [voiceEnabled, currentLanguage]);
  
  useEffect(() => {
    const stepKeyMap = {
      [-1]: 'visaDuration',
      0: isFamilyVisa ? 'familyStep0' : isVisaChange ? 'visaChangeTransport' : 'visaDuration',
      1: isFamilyVisa ? 'familyStep1' : 'step1', 
      2: isFamilyVisa ? 'familyStep2' : 'step2', 
      3: isFamilyVisa ? 'familyStep3' : 'step3', 
      4: isFamilyVisa ? 'familyStep4' : 'step4',
      5: 'step5', 6: 'step6', 7: isFreelance ? 'debitCardStep' : 'step7', 8: 'step7'
    };
    const timer = setTimeout(() => speak(getVoiceMessage(stepKeyMap[step])), 600);
    return () => { 
      clearTimeout(timer); 
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    };
  }, [step, speak, getVoiceMessage, isVisaChange, isFamilyVisa, isFreelance]);
  
  const toggleVoice = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setVoiceEnabled(!voiceEnabled);
    setIsSpeaking(false);
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleFileUpload = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) { alert('Please upload a valid image (JPG, PNG)'); return; }
    if (file.size > 5 * 1024 * 1024) { alert('File size must be less than 5MB'); return; }
    const reader = new FileReader();
    reader.onloadend = () => {
      if (type === 'passport') { 
        setPassportFile(file); 
        setPassportPreview(reader.result);
        // Trigger OCR
        runPassportOcr(file);
      }
      else { setPhotoFile(file); setPhotoPreview(reader.result); }
    };
    reader.readAsDataURL(file);
  };
  
  const runPassportOcr = async (file) => {
    setOcrLoading(true);
    setOcrDone(false);
    setOcrError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/ocr/passport`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000
      });
      const d = res.data;
      
      // Check if it's a valid passport
      if (d.is_passport === false) {
        setOcrError(t('notPassportError') || 'This does not appear to be a passport. Please upload your passport.');
        setPassportFile(null);
        setPassportPreview(null);
        if (passportInputRef.current) passportInputRef.current.value = '';
        setOcrDone(false);
        setOcrLoading(false);
        return;
      }
      
      // Check expiry date validity (must be > 6 months from now)
      if (d.expiry_date) {
        const expiry = new Date(d.expiry_date);
        const sixMonths = new Date();
        sixMonths.setMonth(sixMonths.getMonth() + 6);
        if (expiry < sixMonths) {
          setOcrError(t('passportExpiringSoon') || 'Your passport expires in less than 6 months. Please renew your passport before applying.');
          setPassportFile(null);
          setPassportPreview(null);
          if (passportInputRef.current) passportInputRef.current.value = '';
          setOcrDone(false);
          setOcrLoading(false);
          return;
        }
      }
      
      setOcrData({
        nationality: d.nationality || '',
        date_of_birth: d.date_of_birth || '',
        passport_number: d.passport_number || '',
        issue_date: d.issue_date || '',
        expiry_date: d.expiry_date || '',
        first_name: d.first_name || d.given_names || '',
        last_name: d.last_name || d.surname || '',
        given_names: d.given_names || '',
        surname: d.surname || ''
      });
      setOcrDone(true);
    } catch (err) {
      console.error('OCR error:', err);
      setOcrDone(true);
    } finally {
      setOcrLoading(false);
    }
  };
  
  const handleOcrChange = (field, value) => {
    setOcrData(prev => ({ ...prev, [field]: value }));
  };
  
  const removeFile = (type) => {
    if (type === 'passport') { 
      setPassportFile(null); setPassportPreview(null); 
      if (passportInputRef.current) passportInputRef.current.value = ''; 
      setOcrData({ nationality: '', date_of_birth: '', passport_number: '', issue_date: '', expiry_date: '', first_name: '', last_name: '', given_names: '', surname: '' }); 
      setOcrDone(false);
      setOcrError('');
    }
    else { setPhotoFile(null); setPhotoPreview(null); if (photoInputRef.current) photoInputRef.current.value = ''; }
  };
  
  const validateStep = () => {
    // Family Visa validation
    if (isFamilyVisa) {
      switch (step) {
        case 0: if (!familyMember) { alert(t('selectFamilyMember') || 'Please select a family member'); return false; } return true;
        case 1: 
          if (!passportFile) { alert(t('pleaseUploadPassport') || 'Please upload passport'); return false; }
          if (!photoFile) { alert(t('pleaseUploadPhoto') || 'Please upload photo'); return false; }
          return true;
        case 2: return true; // Emirates ID preview step
        case 3: 
          if (!formData.phone.trim()) { alert(t('pleaseEnterPhone') || 'Please enter your phone number'); return false; }
          if (!formData.sameAsPhone && !formData.telegram.trim()) { alert(t('pleaseEnterTelegram') || 'Please enter your Telegram or WhatsApp number, or check "same number".'); return false; }
          if (!formData.email.trim()) { alert(t('pleaseEnterEmail') || 'Please enter your email'); return false; }
          return true;
        case 4: return true; // Confirm step
        default: return true;
      }
    }
    
    switch (step) {
      case -1: return !!visaDuration;
      case 0: 
        if (isVisaChange) return !!transportMethod;
        return !!visaDuration;
      case 1: if (!formData.firstName.trim() || !formData.lastName.trim()) { alert(t('pleaseEnterName') || 'Please enter your first and last name'); return false; } return true;
      case 2: if (!formData.dateOfPlanning) { alert('Please select a date'); return false; } return true;
      case 3:
        if (!formData.phone.trim()) { alert(t('pleaseEnterPhone') || 'Please enter your phone number'); return false; }
        if (!formData.sameAsPhone && !formData.telegram.trim()) { alert(t('pleaseEnterTelegram') || 'Please enter your Telegram or WhatsApp number, or check "same number".'); return false; }
        return true;
      case 4: if (!formData.email.trim()) { alert(t('pleaseEnterEmail') || 'Please enter your email'); return false; } return true;
      case 5: if (!passportFile) { alert(t('pleaseUploadPassport') || 'Please upload your passport'); return false; } return true;
      case 6: if (!photoFile) { alert(t('pleaseUploadPhoto') || 'Please upload your photo'); return false; } return true;
      case 7: if (isFreelance && wantsDebitCard === null) { alert(t('pleaseSelectDebitCard') || 'Please select Yes or No'); return false; } return true;
      default: return true;
    }
  };
  
  const handleNext = () => { 
    if (validateStep()) {
      setStep(prev => {
        const next = prev + 1;
        if (isFreelance && next === 2) return 3;
        return next;
      });
    }
  };
  const handleBack = () => { 
    setStep(prev => {
      const back = prev - 1;
      if (isFreelance && back === 2) return 1;
      return back;
    });
  };
  
  const handleSubmit = async () => {
    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('service', getDisplayServiceName());
      uploadData.append('servicePrice', getDisplayPrice());
      uploadData.append('date', formData.dateOfPlanning || new Date().toISOString().split('T')[0]);
      
      // For Family Visa, use OCR data for name
      if (isFamilyVisa) {
        const firstName = ocrData.first_name || formData.firstName || '';
        const lastName = ocrData.last_name || formData.lastName || '';
        uploadData.append('name', `${firstName} ${lastName}`.trim() || 'Family Visa Applicant');
        uploadData.append('familyMember', familyMember || '');
      } else {
        uploadData.append('name', `${formData.firstName} ${formData.lastName}`);
      }
      
      uploadData.append('mobile', formData.phone || user?.mobile || '');
      uploadData.append('email', formData.email || user?.email || '');
      // Telegram value: either explicitly entered, or same as phone if user
      // checked "same number for Telegram/WhatsApp".
      const telegramValue = formData.sameAsPhone
        ? (formData.phone || '').trim()
        : (formData.telegram || '').trim();
      const contactValue = telegramValue;
      if (telegramValue) {
        uploadData.append('telegram', telegramValue);
      }
      uploadData.append('nationality', ocrData.nationality || '');
      if (isVisaChange && transportMethod) uploadData.append('transportMethod', transportMethod);
      if (isVisaChange && visaDuration) uploadData.append('visaDuration', visaDuration);
      if (isFreelance && wantsDebitCard) uploadData.append('debitCard', 'yes');
      if (isFreelance && wantsDebitCard) uploadData.append('debitCardFee', '500');
      if (ocrData.passport_number) uploadData.append('passportNumber', ocrData.passport_number);
      if (ocrData.date_of_birth) uploadData.append('dateOfBirth', ocrData.date_of_birth);
      if (ocrData.nationality) uploadData.append('ocrNationality', ocrData.nationality);
      if (ocrData.issue_date) uploadData.append('passportIssueDate', ocrData.issue_date);
      if (ocrData.expiry_date) uploadData.append('passportExpiryDate', ocrData.expiry_date);
      if (passportFile) uploadData.append('passport', passportFile);
      if (photoFile) uploadData.append('photo', photoFile);
      
      const headers = { 'Content-Type': 'multipart/form-data' };
      if (token) headers.Authorization = `Bearer ${token}`;
      
      const response = await axios.post(`${BACKEND_URL}/api/bookings/with-documents`, uploadData, { headers });
      const bookingNumber = response.data.bookingNumber;
      const tgDeepLink = `https://t.me/Visadubaibot?start=${bookingNumber}`;
      
      // If the user provided a Telegram handle (starts with @ or /), prompt
      // them to /start the bot now so they get an instant booking-received
      // confirmation message + auto-mirror of all admin documents.
      const looksLikeTelegram = /^@/.test(contactValue || '') || /telegram/i.test(formData.phone || '');
      
      const baseMsg = `${t('bookingSuccess') || 'Booking successful!'}\n\n${t('bookingNumber') || 'Booking Number'}: ${bookingNumber}\n\n${t('weWillContactYou24h') || 'We will contact you within 24 hours.'}`;
      
      if (looksLikeTelegram) {
        const ok = window.confirm(
          `${baseMsg}\n\n📲 ${t('connectTelegramPrompt') || 'Tap OK to open @Visadubaibot on Telegram and receive your visa documents directly here. (Press Start in the bot to confirm.)'}`
        );
        if (ok) {
          window.open(tgDeepLink, '_blank', 'noopener,noreferrer');
        }
      } else {
        alert(baseMsg);
      }
      onClose();
      window.location.reload();
    } catch (error) {
      console.error('Booking error:', error);
      alert(t('bookingFailed') || 'Booking failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Map step number to progress dot index
  const getProgressSteps = () => {
    if (isFamilyVisa) return [0,1,2,3,4];
    if (isDubaiVisa) return [0,1,2,3,4,5,6,7];
    if (isVisaChange) return [-1,0,1,2,3,4,5,6,7];
    if (isFreelance) return [1,3,4,5,6,7,8];
    return [1,2,3,4,5,6,7];
  };

  const lastStep = isFamilyVisa ? 4 : isFreelance ? 8 : 7;
  const firstStep = isFamilyVisa ? 0 : isDubaiVisa ? 0 : isVisaChange ? -1 : 1;
  
  return (
    <div className="modal-overlay" onClick={onClose} data-testid="step-booking-modal">
      <div className="modal-content step-booking-modal" onClick={(e) => e.stopPropagation()}>
        {/* Cinematic background overlays — purely decorative */}
        <div className="sbm-aurora" aria-hidden="true">
          <span className="sbm-aurora-blob sbm-aurora-1"></span>
          <span className="sbm-aurora-blob sbm-aurora-2"></span>
          <span className="sbm-aurora-blob sbm-aurora-3"></span>
          <span className="sbm-grain"></span>
        </div>
        <div className="sbm-glow-border" aria-hidden="true"></div>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-title-section">
            <h2 className="modal-title">{t('bookYourVisaService') || 'Book Your Visa Service'}</h2>
            <p className="modal-subtitle">
              {getDisplayServiceName()}
              {visaDuration && <span style={{ color: '#5EFCE8', marginLeft: '12px', fontWeight: '600' }}>{getDisplayPrice()}</span>}
            </p>
          </div>
          <div className="modal-header-actions">
            <button className={`voice-toggle-btn ${voiceEnabled ? 'active' : ''}`} onClick={toggleVoice} data-testid="voice-toggle-btn">
              {voiceEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
              {isSpeaking && <span className="speaking-indicator"></span>}
            </button>
            <button className="modal-close" onClick={onClose} data-testid="close-modal-btn"><X size={24} /></button>
          </div>
        </div>
        
        {/* Progress Steps */}
        <div className="step-progress-bar">
          {getProgressSteps().map((s, i) => (
            <div key={s} className={`step-dot ${step >= s ? 'active' : ''} ${step === s ? 'current' : ''}`}>
              {step > s ? <Check size={12} /> : i + 1}
            </div>
          ))}
        </div>
        
        {voiceError && (
          <div className="voice-error-notice" style={{ background: 'rgba(255, 193, 7, 0.15)', border: '1px solid rgba(255, 193, 7, 0.3)', borderRadius: '8px', padding: '10px 16px', margin: '0 16px 8px', fontSize: '13px', color: '#ffc107', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <VolumeX size={16} />
            <span>{currentLanguage === 'ru' ? 'Голосовые сообщения недоступны.' : currentLanguage === 'uz' ? "Ovozli xabarlar mavjud emas." : 'Voice not available.'}</span>
          </div>
        )}
        
        {/* Step Content — `key` re-mounts on step change so the slide-in
            animation defined in CSS plays for every navigation. */}
        <div className="step-content" key={`step-${step}`}>
          
          {/* ============ FAMILY VISA STEPS ============ */}
          
          {/* Family Visa Step 0: Select Family Member */}
          {step === 0 && isFamilyVisa && (
            <div className="step-form" data-testid="step-family-member">
              <AnimatedStepIcon stepType="name" />
              <h3 className="step-title">{t('whoApplyVisaFor') || 'Who do you want to apply visa for?'}</h3>
              <p className="step-instruction">{t('selectFamilyMember') || 'Select a family member'}</p>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginTop: '24px', maxWidth: '500px', margin: '24px auto 0' }}>
                {[
                  { id: 'husband', label: t('husband') || 'Husband', icon: '👨' },
                  { id: 'son', label: t('son') || 'Son', icon: '👦' },
                  { id: 'daughter', label: t('daughter') || 'Daughter', icon: '👧' },
                  { id: 'mother', label: t('mother') || 'Mother', icon: '👩' }
                ].map(member => (
                  <button
                    key={member.id}
                    onClick={() => setFamilyMember(member.id)}
                    data-testid={`family-${member.id}-btn`}
                    style={{
                      padding: '24px 16px',
                      background: familyMember === member.id ? 'rgba(94, 252, 232, 0.15)' : (isLight ? 'rgba(94, 252, 232, 0.08)' : 'rgba(255,255,255,0.05)'),
                      border: familyMember === member.id ? '2px solid #5EFCE8' : (isLight ? '2px solid rgba(94, 252, 232, 0.25)' : '2px solid rgba(255,255,255,0.1)'),
                      borderRadius: '16px', cursor: 'pointer', transition: 'all 0.3s ease',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px'
                    }}
                  >
                    <span style={{ fontSize: '40px' }}>{member.icon}</span>
                    <span style={{ fontSize: '18px', fontWeight: '600', color: familyMember === member.id ? '#5EFCE8' : 'var(--text-primary)' }}>
                      {member.label}
                    </span>
                    {familyMember === member.id && <Check size={20} style={{ color: '#5EFCE8' }} />}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Family Visa Step 1: Passport & Photo Upload */}
          {step === 1 && isFamilyVisa && (
            <div className="step-form" data-testid="step-family-passport">
              <AnimatedStepIcon stepType="passport" />
              <h3 className="step-title">{t('uploadPassportPhoto') || 'Upload Passport & Photo'}</h3>
              <p className="step-instruction">{t('uploadPassportPhotoOf') || 'Please upload passport and personal photo of'} {t(familyMember) || familyMember}</p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginTop: '24px' }}>
                {/* Passport Upload */}
                <div className="file-upload-box">
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#5EFCE8' }}>
                    <FileText size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    {t('passport') || 'Passport'}
                  </label>
                  {!passportPreview ? (
                    <div 
                      className="upload-dropzone"
                      onClick={() => passportInputRef.current?.click()}
                      style={{
                        border: '2px dashed rgba(94, 252, 232, 0.3)',
                        borderRadius: '12px',
                        padding: '32px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        background: 'rgba(94, 252, 232, 0.05)',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <Upload size={32} style={{ color: '#5EFCE8', marginBottom: '12px' }} />
                      <p style={{ color: '#94a3b8', margin: 0 }}>{t('clickUploadPassport') || 'Click to upload passport'}</p>
                      <input
                        ref={passportInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, 'passport')}
                        style={{ display: 'none' }}
                      />
                    </div>
                  ) : (
                    <div style={{ position: 'relative' }}>
                      <img src={passportPreview} alt="Passport" style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: '8px' }} />
                      <button onClick={() => removeFile('passport')} style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(255,0,0,0.8)', border: 'none', borderRadius: '50%', padding: '8px', cursor: 'pointer' }}>
                        <Trash2 size={16} color="#fff" />
                      </button>
                      {ocrLoading && (
                        <div style={{ textAlign: 'center', marginTop: '12px', padding: '12px', background: 'rgba(94, 252, 232, 0.1)', borderRadius: '8px' }}>
                          <Loader2 className="animate-spin" style={{ color: '#5EFCE8', marginBottom: '8px' }} />
                          <p style={{ color: '#5EFCE8', margin: 0, fontSize: '14px' }}>{t('readingPassportData') || 'Reading passport data...'}</p>
                        </div>
                      )}
                      {ocrDone && !ocrLoading && (ocrData.first_name || ocrData.nationality) && (
                        <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(94, 252, 232, 0.1)', borderRadius: '8px', border: '1px solid rgba(94, 252, 232, 0.3)' }}>
                          <p style={{ color: '#5EFCE8', margin: '0 0 8px', fontSize: '13px', fontWeight: '600' }}>✓ {t('dataExtracted') || 'Data Extracted'}:</p>
                          <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                            {ocrData.first_name && <p style={{ margin: '4px 0' }}>{t('name') || 'Name'}: <span style={{ color: 'var(--text-primary)' }}>{ocrData.first_name} {ocrData.last_name}</span></p>}
                            {ocrData.nationality && <p style={{ margin: '4px 0' }}>{t('nationalityLabel') || 'Nationality'}: <span style={{ color: 'var(--text-primary)' }}>{ocrData.nationality}</span></p>}
                            {ocrData.date_of_birth && <p style={{ margin: '4px 0' }}>{t('dateOfBirthLabel') || 'DOB'}: <span style={{ color: 'var(--text-primary)' }}>{ocrData.date_of_birth}</span></p>}
                          </div>
                        </div>
                      )}
                      {ocrError && (
                        <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(255, 0, 0, 0.1)', borderRadius: '8px', border: '1px solid rgba(255, 0, 0, 0.3)' }}>
                          <p style={{ color: '#ff6b6b', margin: 0, fontSize: '13px' }}>⚠️ {ocrError}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Photo Upload */}
                <div className="file-upload-box">
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#5EFCE8' }}>
                    <Camera size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    {t('personalPhoto') || 'Personal Photo'}
                  </label>
                  {!photoPreview ? (
                    <div 
                      className="upload-dropzone"
                      onClick={() => photoInputRef.current?.click()}
                      style={{
                        border: '2px dashed rgba(94, 252, 232, 0.3)',
                        borderRadius: '12px',
                        padding: '32px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        background: 'rgba(94, 252, 232, 0.05)',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <Upload size={32} style={{ color: '#5EFCE8', marginBottom: '12px' }} />
                      <p style={{ color: '#94a3b8', margin: 0 }}>{t('clickUploadPhoto') || 'Click to upload photo'}</p>
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, 'photo')}
                        style={{ display: 'none' }}
                      />
                    </div>
                  ) : (
                    <div style={{ position: 'relative' }}>
                      <img src={photoPreview} alt="Photo" style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: '8px' }} />
                      <button onClick={() => removeFile('photo')} style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(255,0,0,0.8)', border: 'none', borderRadius: '50%', padding: '8px', cursor: 'pointer' }}>
                        <Trash2 size={16} color="#fff" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Family Visa Step 2: Emirates ID Preview */}
          {step === 2 && isFamilyVisa && (
            <div className="step-form" data-testid="step-family-emirates-id">
              <AnimatedStepIcon stepType="review" />
              <h3 className="step-title">{t('emiratesIdPreview') || 'Emirates ID Preview'}</h3>
              <p className="step-instruction">{t('reviewPassportInfo') || 'Review the information extracted from passport'}</p>
              
              <div style={{
                background: 'linear-gradient(135deg, #1a2744 0%, #0d1f3c 100%)',
                borderRadius: '16px',
                padding: '24px',
                marginTop: '24px',
                border: '2px solid rgba(94, 252, 232, 0.3)',
                maxWidth: '450px',
                margin: '24px auto 0'
              }}>
                {/* Emirates ID Card Design */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                  {/* Photo */}
                  <div style={{
                    width: '100px',
                    height: '120px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '2px solid #5EFCE8',
                    flexShrink: 0
                  }}>
                    {photoPreview ? (
                      <img src={photoPreview} alt="ID Photo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', background: '#2a3a5a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <User size={40} color="#5EFCE8" />
                      </div>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: '12px' }}>
                      <span style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase' }}>{t('firstNameLabel') || 'First Name'}</span>
                      <p style={{ color: 'var(--text-primary)', fontWeight: '600', margin: '4px 0 0', fontSize: '16px' }}>
                        {ocrData.first_name || ocrData.given_names || formData.firstName || '—'}
                      </p>
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <span style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase' }}>{t('lastNameLabel') || 'Last Name'}</span>
                      <p style={{ color: 'var(--text-primary)', fontWeight: '600', margin: '4px 0 0', fontSize: '16px' }}>
                        {ocrData.last_name || ocrData.surname || formData.lastName || '—'}
                      </p>
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <span style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase' }}>{t('dateOfBirthLabel') || 'Date of Birth'}</span>
                      <p style={{ color: 'var(--text-primary)', fontWeight: '600', margin: '4px 0 0', fontSize: '16px' }}>
                        {ocrData.date_of_birth || '—'}
                      </p>
                    </div>
                    <div>
                      <span style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase' }}>{t('nationalityLabel') || 'Nationality'}</span>
                      <p style={{ color: '#5EFCE8', fontWeight: '600', margin: '4px 0 0', fontSize: '16px' }}>
                        {ocrData.nationality || '—'}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Footer */}
                <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#64748b', fontSize: '12px' }}>{t('familyVisa') || 'Family Visa'} - {t(familyMember) || familyMember?.charAt(0).toUpperCase() + familyMember?.slice(1)}</span>
                  <span style={{ color: '#5EFCE8', fontSize: '12px', fontWeight: '600' }}>{t('unitedArabEmirates') || 'UNITED ARAB EMIRATES'}</span>
                </div>
              </div>
              
              {!ocrData.nationality && !ocrData.date_of_birth && (
                <p style={{ color: '#ffc107', textAlign: 'center', marginTop: '16px', fontSize: '14px' }}>
                  ⚠️ {t('goBackUploadClear') || 'Please go back and upload a clear passport photo to extract information'}
                </p>
              )}
            </div>
          )}
          
          {/* Family Visa Step 3: Contact Information */}
          {step === 3 && isFamilyVisa && (
            <div className="step-form" data-testid="step-family-contact">
              <AnimatedStepIcon stepType="phone" />
              <h3 className="step-title">{t('contactInfo') || 'Contact Information'}</h3>
              <p className="step-instruction">{t('enterContactDetails') || 'Please enter your contact details so we can reach you'}</p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '24px', maxWidth: '440px', margin: '24px auto 0' }}>
                {/* Phone */}
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontWeight: '500', color: '#5EFCE8', fontSize: '14px' }}>
                    <Phone size={16} />
                    {t('phoneNumber') || 'Phone Number'} *
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+971 50 123 4567"
                    data-testid="family-phone-input"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '2px solid rgba(94, 252, 232, 0.3)',
                      borderRadius: '12px',
                      color: 'var(--text-primary)',
                      fontSize: '16px',
                      outline: 'none',
                      transition: 'all 0.3s ease'
                    }}
                  />
                </div>
                
                {/* Same-number toggle */}
                <label
                  className="step-same-toggle"
                  data-testid="family-same-as-phone-toggle"
                >
                  <input
                    type="checkbox"
                    checked={!!formData.sameAsPhone}
                    onChange={(e) => setFormData({ ...formData, sameAsPhone: e.target.checked, telegram: e.target.checked ? '' : formData.telegram })}
                  />
                  <Send size={13} className="step-same-toggle-icon" />
                  {t('sameNumberForTelegram') || 'I use the same number for Telegram / WhatsApp'}
                </label>
                
                {/* Telegram/WhatsApp — hidden when sameAsPhone is checked */}
                {!formData.sameAsPhone && (
                  <div data-testid="family-telegram-field-wrapper">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontWeight: '500', color: '#229ED9', fontSize: '14px' }}>
                      <Send size={16} />
                      {t('telegramOrWhatsapp') || 'Telegram or WhatsApp Number'} *
                    </label>
                    <input
                      type="text"
                      value={formData.telegram}
                      onChange={(e) => setFormData({...formData, telegram: e.target.value})}
                      placeholder="@username or +971 50 123 4567"
                      data-testid="family-telegram-input"
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        background: 'rgba(255,255,255,0.05)',
                        border: '2px solid rgba(34, 158, 217, 0.35)',
                        borderRadius: '12px',
                        color: 'var(--text-primary)',
                        fontSize: '16px',
                        outline: 'none',
                        transition: 'all 0.3s ease'
                      }}
                    />
                    <p style={{ color: '#64748b', fontSize: '12px', marginTop: '6px' }}>
                      {t('telegramShareDocsHint') || 'We will share your approved documents to this Telegram/WhatsApp.'}
                    </p>
                  </div>
                )}
                
                {/* Email */}
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontWeight: '500', color: '#5EFCE8', fontSize: '14px' }}>
                    <Mail size={16} />
                    {t('emailAddress') || 'Email Address'} *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="example@email.com"
                    data-testid="family-email-input"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '2px solid rgba(94, 252, 232, 0.3)',
                      borderRadius: '12px',
                      color: 'var(--text-primary)',
                      fontSize: '16px',
                      outline: 'none',
                      transition: 'all 0.3s ease'
                    }}
                  />
                </div>
                
                <p style={{ color: '#64748b', fontSize: '12px', textAlign: 'center', marginTop: '8px' }}>
                  {t('weWillContactYou') || 'We will contact you via WhatsApp or Telegram for updates on your application'}
                </p>
              </div>
            </div>
          )}
          
          {/* Family Visa Step 4: Confirmation */}
          {step === 4 && isFamilyVisa && (
            <div className="step-form" data-testid="step-family-confirm">
              <AnimatedStepIcon stepType="review" />
              <h3 className="step-title">{t('confirmApplication') || 'Confirm Application'}</h3>
              <p className="step-instruction">{t('reviewSubmitFamilyVisa') || 'Please review and submit your Family Visa application'}</p>
              
              <div style={{
                background: 'rgba(94, 252, 232, 0.1)',
                borderRadius: '16px',
                padding: '24px',
                marginTop: '24px',
                border: '1px solid rgba(94, 252, 232, 0.3)'
              }}>
                <div style={{ display: 'grid', gap: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '12px' }}>
                    <span style={{ color: '#94a3b8' }}>{t('service') || 'Service'}</span>
                    <span style={{ color: 'var(--text-primary)', fontWeight: '600' }}>{t('familyVisa') || 'Family Visa'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '12px' }}>
                    <span style={{ color: '#94a3b8' }}>{t('applyingFor') || 'Applying For'}</span>
                    <span style={{ color: '#5EFCE8', fontWeight: '600' }}>{t(familyMember) || familyMember?.charAt(0).toUpperCase() + familyMember?.slice(1)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '12px' }}>
                    <span style={{ color: '#94a3b8' }}>{t('name') || 'Name'}</span>
                    <span style={{ color: 'var(--text-primary)' }}>{ocrData.first_name || ocrData.given_names || '—'} {ocrData.last_name || ocrData.surname || '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '12px' }}>
                    <span style={{ color: '#94a3b8' }}>{t('nationalityLabel') || 'Nationality'}</span>
                    <span style={{ color: 'var(--text-primary)' }}>{ocrData.nationality || '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '12px' }}>
                    <span style={{ color: '#94a3b8' }}>{t('phoneNumber') || 'Phone'}</span>
                    <span style={{ color: 'var(--text-primary)' }}>{formData.phone || '—'}</span>
                  </div>
                  {!formData.sameAsPhone && formData.telegram && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '12px' }}>
                      <span style={{ color: '#94a3b8' }}>{t('telegramOrWhatsapp') || 'Telegram / WhatsApp'}</span>
                      <span style={{ color: '#229ED9', fontWeight: '600' }}>{formData.telegram}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '12px' }}>
                    <span style={{ color: '#94a3b8' }}>{t('emailAddress') || 'Email'}</span>
                    <span style={{ color: 'var(--text-primary)' }}>{formData.email || '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#94a3b8' }}>{t('price') || 'Price'}</span>
                    <span style={{ color: '#5EFCE8', fontWeight: '700', fontSize: '20px' }}>{selectedService?.price || 'AED 2,500'}</span>
                  </div>
                </div>
              </div>
              
              <p style={{ color: '#94a3b8', textAlign: 'center', marginTop: '16px', fontSize: '13px' }}>
                {t('bySubmittingAgree') || 'By submitting, you agree to our terms and conditions. We will contact you within 24 hours.'}
              </p>
            </div>
          )}
          
          {/* ============ END FAMILY VISA STEPS ============ */}
          
          {/* Step -1: Visa Change Duration Selection */}
          {step === -1 && isVisaChange && (
            <div className="step-form" data-testid="step-vc-duration">
              <AnimatedStepIcon stepType="duration" />
              <h3 className="step-title">{t('selectVisaDuration') || 'Select Visa Duration'}</h3>
              <p className="step-instruction">{t('chooseVisaDuration') || 'Choose your preferred visa duration'}</p>
              
              <div style={{ display: 'flex', gap: '16px', marginTop: '24px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setVisaDuration('30')}
                  data-testid="vc-30-days-btn"
                  style={{
                    flex: '1', minWidth: '200px', maxWidth: '280px', padding: '24px 20px',
                    background: visaDuration === '30' ? 'rgba(94, 252, 232, 0.15)' : (isLight ? 'rgba(94, 252, 232, 0.08)' : 'rgba(255,255,255,0.05)'),
                    border: visaDuration === '30' ? '2px solid #5EFCE8' : (isLight ? '2px solid rgba(94, 252, 232, 0.25)' : '2px solid rgba(255,255,255,0.1)'),
                    borderRadius: '16px', cursor: 'pointer', transition: 'all 0.3s ease',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px'
                  }}
                >
                  <span style={{ fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)' }}>30 {t('days') || 'Days'}</span>
                  <span style={{ fontSize: '24px', fontWeight: '700', color: '#5EFCE8' }}>AED 500</span>
                  {visaDuration === '30' && <Check size={24} style={{ color: '#5EFCE8' }} />}
                </button>
                
                <button
                  onClick={() => setVisaDuration('60')}
                  data-testid="vc-60-days-btn"
                  style={{
                    flex: '1', minWidth: '200px', maxWidth: '280px', padding: '24px 20px',
                    background: visaDuration === '60' ? 'rgba(94, 252, 232, 0.15)' : (isLight ? 'rgba(94, 252, 232, 0.08)' : 'rgba(255,255,255,0.05)'),
                    border: visaDuration === '60' ? '2px solid #5EFCE8' : (isLight ? '2px solid rgba(94, 252, 232, 0.25)' : '2px solid rgba(255,255,255,0.1)'),
                    borderRadius: '16px', cursor: 'pointer', transition: 'all 0.3s ease',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px'
                  }}
                >
                  <span style={{ fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)' }}>60 {t('days') || 'Days'}</span>
                  <span style={{ fontSize: '24px', fontWeight: '700', color: '#5EFCE8' }}>AED 800</span>
                  {visaDuration === '60' && <Check size={24} style={{ color: '#5EFCE8' }} />}
                </button>
              </div>
            </div>
          )}

          {/* Step 0: Visa Duration Selection (Dubai Visa only) */}
          {step === 0 && isDubaiVisa && (
            <div className="step-form" data-testid="step-duration">
              <AnimatedStepIcon stepType="duration" />
              <h3 className="step-title">{t('selectVisaDuration') || 'Select Visa Duration'}</h3>
              <p className="step-instruction">{t('chooseVisaDuration') || 'Choose your preferred visa duration'}</p>
              
              <div style={{ display: 'flex', gap: '16px', marginTop: '24px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setVisaDuration('30')}
                  data-testid="visa-30-days-btn"
                  style={{
                    flex: '1', minWidth: '200px', maxWidth: '280px', padding: '24px 20px',
                    background: visaDuration === '30' ? 'rgba(94, 252, 232, 0.15)' : (isLight ? 'rgba(94, 252, 232, 0.08)' : 'rgba(255,255,255,0.05)'),
                    border: visaDuration === '30' ? '2px solid #5EFCE8' : (isLight ? '2px solid rgba(94, 252, 232, 0.25)' : '2px solid rgba(255,255,255,0.1)'),
                    borderRadius: '16px', cursor: 'pointer', transition: 'all 0.3s ease',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px'
                  }}
                >
                  <span style={{ fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)' }}>30 {t('days') || 'Days'}</span>
                  <span style={{ fontSize: '24px', fontWeight: '700', color: '#5EFCE8' }}>AED 500</span>
                  {visaDuration === '30' && <Check size={24} style={{ color: '#5EFCE8' }} />}
                </button>
                
                <button
                  onClick={() => setVisaDuration('60')}
                  data-testid="visa-60-days-btn"
                  style={{
                    flex: '1', minWidth: '200px', maxWidth: '280px', padding: '24px 20px',
                    background: visaDuration === '60' ? 'rgba(94, 252, 232, 0.15)' : (isLight ? 'rgba(94, 252, 232, 0.08)' : 'rgba(255,255,255,0.05)'),
                    border: visaDuration === '60' ? '2px solid #5EFCE8' : (isLight ? '2px solid rgba(94, 252, 232, 0.25)' : '2px solid rgba(255,255,255,0.1)'),
                    borderRadius: '16px', cursor: 'pointer', transition: 'all 0.3s ease',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px'
                  }}
                >
                  <span style={{ fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)' }}>60 {t('days') || 'Days'}</span>
                  <span style={{ fontSize: '24px', fontWeight: '700', color: '#5EFCE8' }}>AED 800</span>
                  {visaDuration === '60' && <Check size={24} style={{ color: '#5EFCE8' }} />}
                </button>
              </div>
            </div>
          )}

          {/* Step 0: Transport Method (Visa Change only) */}
          {step === 0 && isVisaChange && (
            <div className="step-form" data-testid="step-vc-transport">
              <AnimatedStepIcon stepType="transport" />
              <h3 className="step-title">{t('chooseTransport') || 'How do you want to do your visa change?'}</h3>
              <p className="step-instruction">{t('selectTransportMethod') || 'Please choose one'}</p>
              
              {visaDuration && (
                <div style={{ textAlign: 'center', marginBottom: '16px', color: 'rgba(255,255,255,0.7)', fontSize: '15px' }}>
                  Visa {visaDuration} Days: <span style={{ color: '#5EFCE8', fontWeight: '600' }}>AED {visaDuration === '30' ? '500' : '800'}</span>
                </div>
              )}
              
              <div style={{ display: 'flex', gap: '12px', marginTop: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setTransportMethod('car')}
                  data-testid="transport-car-btn"
                  style={{
                    flex: '1', minWidth: '140px', maxWidth: '200px', padding: '20px 16px',
                    background: transportMethod === 'car' ? 'rgba(94, 252, 232, 0.15)' : (isLight ? 'rgba(94, 252, 232, 0.08)' : 'rgba(255,255,255,0.05)'),
                    border: transportMethod === 'car' ? '2px solid #5EFCE8' : (isLight ? '2px solid rgba(94, 252, 232, 0.25)' : '2px solid rgba(255,255,255,0.1)'),
                    borderRadius: '16px', cursor: 'pointer', transition: 'all 0.3s ease',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px'
                  }}
                >
                  <Car size={32} style={{ color: '#5EFCE8' }} />
                  <span style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>By Car</span>
                  <span style={{ fontSize: '14px', color: '#5EFCE8', fontWeight: '600' }}>+AED 500</span>
                  {transportMethod === 'car' && <Check size={20} style={{ color: '#5EFCE8' }} />}
                </button>
                
                <button
                  onClick={() => setTransportMethod('bus')}
                  data-testid="transport-bus-btn"
                  style={{
                    flex: '1', minWidth: '140px', maxWidth: '200px', padding: '20px 16px',
                    background: transportMethod === 'bus' ? 'rgba(94, 252, 232, 0.15)' : (isLight ? 'rgba(94, 252, 232, 0.08)' : 'rgba(255,255,255,0.05)'),
                    border: transportMethod === 'bus' ? '2px solid #5EFCE8' : (isLight ? '2px solid rgba(94, 252, 232, 0.25)' : '2px solid rgba(255,255,255,0.1)'),
                    borderRadius: '16px', cursor: 'pointer', transition: 'all 0.3s ease',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px'
                  }}
                >
                  <Bus size={32} style={{ color: '#5EFCE8' }} />
                  <span style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>By Bus</span>
                  <span style={{ fontSize: '14px', color: '#5EFCE8', fontWeight: '600' }}>+AED 250</span>
                  {transportMethod === 'bus' && <Check size={20} style={{ color: '#5EFCE8' }} />}
                </button>
                
                <button
                  onClick={() => setTransportMethod('airplane')}
                  data-testid="transport-airplane-btn"
                  style={{
                    flex: '1', minWidth: '140px', maxWidth: '200px', padding: '20px 16px',
                    background: transportMethod === 'airplane' ? 'rgba(94, 252, 232, 0.15)' : (isLight ? 'rgba(94, 252, 232, 0.08)' : 'rgba(255,255,255,0.05)'),
                    border: transportMethod === 'airplane' ? '2px solid #5EFCE8' : (isLight ? '2px solid rgba(94, 252, 232, 0.25)' : '2px solid rgba(255,255,255,0.1)'),
                    borderRadius: '16px', cursor: 'pointer', transition: 'all 0.3s ease',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px'
                  }}
                >
                  <Plane size={32} style={{ color: '#5EFCE8' }} />
                  <span style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>By Airplane</span>
                  <span style={{ fontSize: '14px', color: '#5EFCE8', fontWeight: '600' }}>+AED 700</span>
                  {transportMethod === 'airplane' && <Check size={20} style={{ color: '#5EFCE8' }} />}
                </button>
              </div>
              
              {transportMethod && (
                <div style={{ textAlign: 'center', marginTop: '20px', padding: '12px 20px', background: 'rgba(94, 252, 232, 0.1)', borderRadius: '12px', border: '1px solid rgba(94, 252, 232, 0.2)' }}>
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>Total Price: </span>
                  <span style={{ color: '#5EFCE8', fontSize: '22px', fontWeight: '700' }}>{getDisplayPrice()}</span>
                </div>
              )}
            </div>
          )}

          {/* Step 1: First Name + Last Name */}
          {step === 1 && !isFamilyVisa && (
            <div className="step-form" data-testid="step-1">
              <AnimatedStepIcon stepType="name" />
              <h3 className="step-title">{t('firstName') || 'First Name'} & {t('lastName') || 'Last Name'}</h3>
              <p className="step-instruction">{t('enterNamePassport') || 'Enter your name as it appears on your passport'}</p>
              
              <div className="step-input-container" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <input
                  type="text" name="firstName" value={formData.firstName} onChange={handleChange}
                  placeholder={t('firstName') || 'First Name'}
                  className="step-input" autoFocus data-testid="firstname-input"
                  style={{ flex: '1', minWidth: '180px' }}
                />
                <input
                  type="text" name="lastName" value={formData.lastName} onChange={handleChange}
                  placeholder={t('lastName') || 'Last Name'}
                  className="step-input" data-testid="lastname-input"
                  style={{ flex: '1', minWidth: '180px' }}
                />
              </div>
            </div>
          )}

          {/* Step 2: Date of Planning */}
          {step === 2 && !isFamilyVisa && !isFreelance && (
            <div className="step-form" data-testid="step-2">
              <AnimatedStepIcon stepType="date" />
              <h3 className="step-title">{t('dateOfPlanning') || 'Date of Planning'}</h3>
              <p className="step-instruction">{t('selectTravelDate') || 'Select your planned travel date'}</p>
              
              <div className="step-input-container">
                <input
                  type="date" name="dateOfPlanning" value={formData.dateOfPlanning} onChange={handleChange}
                  className="step-input" autoFocus data-testid="date-input"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
          )}

          {/* Step 3: Phone + Telegram/WhatsApp (with same-number toggle) */}
          {step === 3 && !isFamilyVisa && (
            <div className="step-form" data-testid="step-3">
              <AnimatedStepIcon stepType="phone" />
              <h3 className="step-title">{t('contactNumbers') || 'Contact Numbers'}</h3>
              <p className="step-instruction">{t('phoneAndTelegramHint') || 'Enter your phone number and your Telegram/WhatsApp so we can share approved documents.'}</p>
              
              <div className="step-input-container" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {/* Phone */}
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: '#5EFCE8' }}>
                    <Phone size={14} />
                    {t('phoneNumber') || 'Phone Number'} *
                  </label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                    placeholder="+971 50 123 4567" className="step-input" autoFocus data-testid="phone-input" />
                </div>
                
                {/* Same-number toggle (left aligned, below phone) */}
                <label
                  className="step-same-toggle"
                  data-testid="same-as-phone-toggle"
                >
                  <input
                    type="checkbox"
                    checked={!!formData.sameAsPhone}
                    onChange={(e) => setFormData({ ...formData, sameAsPhone: e.target.checked, telegram: e.target.checked ? '' : formData.telegram })}
                  />
                  <Send size={13} className="step-same-toggle-icon" />
                  {t('sameNumberForTelegram') || 'I use the same number for Telegram / WhatsApp'}
                </label>
                
                {/* Telegram/WhatsApp field — hidden when sameAsPhone is checked */}
                {!formData.sameAsPhone && (
                  <div data-testid="telegram-field-wrapper">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: '#229ED9' }}>
                      <Send size={14} />
                      {t('telegramOrWhatsapp') || 'Telegram or WhatsApp Number'} *
                    </label>
                    <input
                      type="text"
                      name="telegram"
                      value={formData.telegram}
                      onChange={handleChange}
                      placeholder="@username or +971 50 123 4567"
                      className="step-input"
                      data-testid="telegram-input"
                      style={{ borderColor: 'rgba(34, 158, 217, 0.35)' }}
                    />
                    <p className="field-note" style={{ color: '#64748b', marginTop: '6px' }}>
                      {t('telegramShareDocsHint') || 'We will share your approved documents to this Telegram/WhatsApp.'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Step 4: Email */}
          {step === 4 && !isFamilyVisa && (
            <div className="step-form" data-testid="step-4">
              <AnimatedStepIcon stepType="email" />
              <h3 className="step-title">{t('emailAddress') || 'Email Address'}</h3>
              <p className="step-instruction">{t('enterEmailConfirmation') || 'Enter your email for booking confirmation'}</p>
              
              <div className="step-input-container">
                <input type="email" name="email" value={formData.email} onChange={handleChange}
                  placeholder="your.email@example.com" className="step-input" autoFocus
                  disabled={!!user} data-testid="email-input" />
                {user && <p className="field-note">{t('usingAccountEmail') || 'Using account email'}: {user.email}</p>}
              </div>
            </div>
          )}
          
          {/* Step 5: Passport Upload + OCR */}
          {step === 5 && !isFamilyVisa && (
            <div className="step-form" data-testid="step-5">
              <AnimatedStepIcon stepType="passport" />
              <h3 className="step-title">{t('uploadPassport') || 'Upload Passport'}</h3>
              <p className="step-instruction">{t('uploadPassportSimple') || 'Upload a clear photo of your passport'}</p>
              
              {!passportPreview ? (
                <div className="upload-zone" onClick={() => passportInputRef.current?.click()} data-testid="passport-upload-zone">
                  <Upload size={48} />
                  <p>{t('clickToUploadPassport') || 'Click to upload passport'}</p>
                  <span className="upload-hint">{t('jpgPng') || 'JPG, PNG (max 5MB)'}</span>
                </div>
              ) : (
                <div>
                  <div className="upload-preview-container">
                    <img src={passportPreview} alt="Passport preview" className="upload-preview-image" />
                    <button className="remove-upload-btn" onClick={() => removeFile('passport')} type="button" data-testid="remove-passport-btn">
                      <Trash2 size={18} /> {t('remove') || 'Remove'}
                    </button>
                  </div>
                  
                  {ocrLoading && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginTop: '16px', padding: '14px', background: 'rgba(94, 252, 232, 0.08)', borderRadius: '12px', border: '1px solid rgba(94, 252, 232, 0.2)' }}>
                      <Loader2 size={20} style={{ color: '#5EFCE8', animation: 'spin 1s linear infinite' }} />
                      <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px' }}>{t('readingPassport') || 'Reading passport data...'}</span>
                    </div>
                  )}
                  
                  {ocrDone && !ocrLoading && (
                    <div style={{ marginTop: '16px', padding: '16px', background: 'rgba(255,255,255,0.04)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)' }} data-testid="ocr-results">
                      <p style={{ color: '#5EFCE8', fontSize: '13px', fontWeight: '600', marginBottom: '12px' }}>
                        {t('passportDataExtracted') || 'Passport data extracted — you can edit if incorrect'}
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div>
                          <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', display: 'block', marginBottom: '4px' }}>{t('nationality') || 'Nationality'}</label>
                          <input type="text" value={ocrData.nationality} onChange={(e) => handleOcrChange('nationality', e.target.value)}
                            className="step-input" data-testid="ocr-nationality" style={{ padding: '10px 14px', fontSize: '14px' }} placeholder={t('nationality') || 'Nationality'} />
                        </div>
                        <div>
                          <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', display: 'block', marginBottom: '4px' }}>{t('dateOfBirth') || 'Date of Birth'}</label>
                          <input type="text" value={ocrData.date_of_birth} onChange={(e) => handleOcrChange('date_of_birth', e.target.value)}
                            className="step-input" data-testid="ocr-dob" style={{ padding: '10px 14px', fontSize: '14px' }} placeholder="YYYY-MM-DD" />
                        </div>
                        <div>
                          <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', display: 'block', marginBottom: '4px' }}>{t('passportNumber') || 'Passport Number'}</label>
                          <input type="text" value={ocrData.passport_number} onChange={(e) => handleOcrChange('passport_number', e.target.value)}
                            className="step-input" data-testid="ocr-passport-number" style={{ padding: '10px 14px', fontSize: '14px' }} placeholder={t('passportNumber') || 'Passport Number'} />
                        </div>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                          <div style={{ flex: 1, minWidth: '120px' }}>
                            <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', display: 'block', marginBottom: '4px' }}>{t('issueDate') || 'Issue Date'}</label>
                            <input type="text" value={ocrData.issue_date} onChange={(e) => handleOcrChange('issue_date', e.target.value)}
                              className="step-input" data-testid="ocr-issue-date" style={{ padding: '10px 14px', fontSize: '14px' }} placeholder="YYYY-MM-DD" />
                          </div>
                          <div style={{ flex: 1, minWidth: '120px' }}>
                            <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', display: 'block', marginBottom: '4px' }}>{t('expiryDate') || 'Expiry Date'}</label>
                            <input type="text" value={ocrData.expiry_date} onChange={(e) => handleOcrChange('expiry_date', e.target.value)}
                              className="step-input" data-testid="ocr-expiry-date" style={{ padding: '10px 14px', fontSize: '14px' }} placeholder="YYYY-MM-DD" />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {ocrError && (
                <div style={{ marginTop: '16px', padding: '14px 18px', background: 'rgba(255, 77, 106, 0.1)', border: '1px solid rgba(255, 77, 106, 0.3)', borderRadius: '12px', textAlign: 'center' }} data-testid="ocr-error">
                  <p style={{ color: '#FF4D6A', fontSize: '14px', fontWeight: '600' }}>{ocrError}</p>
                </div>
              )}
              
              <input ref={passportInputRef} type="file" accept="image/jpeg,image/png,image/jpg"
                onChange={(e) => handleFileUpload(e, 'passport')} style={{ display: 'none' }} />
            </div>
          )}
          
          {/* Step 6: Photo Upload */}
          {step === 6 && !isFamilyVisa && (
            <div className="step-form" data-testid="step-6">
              <AnimatedStepIcon stepType="photo" />
              <h3 className="step-title">{t('uploadPhoto') || 'Upload Photo'}</h3>
              <p className="step-instruction">{t('uploadPhotoInstruction') || 'Upload a clear personal photo for your visa application'}</p>
              
              {!photoPreview ? (
                <div className="upload-zone" onClick={() => photoInputRef.current?.click()} data-testid="photo-upload-zone">
                  <Upload size={48} />
                  <p>{t('clickToUploadPhoto') || 'Click to upload photo'}</p>
                  <span className="upload-hint">{t('jpgPng') || 'JPG, PNG (max 5MB)'}</span>
                </div>
              ) : (
                <div className="upload-preview-container">
                  <img src={photoPreview} alt="Photo preview" className="upload-preview-image photo-preview" />
                  <button className="remove-upload-btn" onClick={() => removeFile('photo')} type="button" data-testid="remove-photo-btn">
                    <Trash2 size={18} /> {t('remove') || 'Remove'}
                  </button>
                </div>
              )}
              <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/jpg"
                onChange={(e) => handleFileUpload(e, 'photo')} style={{ display: 'none' }} />
            </div>
          )}
          
          {/* Step 7: Debit Card Option (Freelance Only) */}
          {step === 7 && isFreelance && !isFamilyVisa && (
            <div className="step-form" data-testid="step-debit-card">
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
                <CreditCard size={40} style={{ color: '#5EFCE8' }} />
              </div>
              <h3 className="step-title">{t('debitCardQuestion') || 'Do you need a Debit Card?'}</h3>
              <p className="step-instruction">{t('debitCardDescription') || 'Get a UAE bank debit card along with your freelance visa for an additional 500 AED'}</p>
              
              <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', marginTop: '32px' }}>
                {/* YES Button */}
                <button
                  data-testid="debit-card-yes"
                  onClick={() => setWantsDebitCard(true)}
                  style={{
                    flex: '1',
                    maxWidth: '200px',
                    padding: '20px 24px',
                    borderRadius: '16px',
                    border: wantsDebitCard === true ? '2px solid #5EFCE8' : '2px solid rgba(255,255,255,0.15)',
                    background: wantsDebitCard === true ? 'rgba(94, 252, 232, 0.12)' : 'rgba(255,255,255,0.04)',
                    color: wantsDebitCard === true ? '#5EFCE8' : 'rgba(255,255,255,0.7)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '10px',
                    fontSize: '16px',
                    fontWeight: '600'
                  }}
                >
                  <Check size={28} />
                  {t('yes') || 'Yes'}
                  <span style={{ fontSize: '12px', fontWeight: '400', opacity: 0.7 }}>+500 AED</span>
                </button>

                {/* NO Button */}
                <button
                  data-testid="debit-card-no"
                  onClick={() => setWantsDebitCard(false)}
                  style={{
                    flex: '1',
                    maxWidth: '200px',
                    padding: '20px 24px',
                    borderRadius: '16px',
                    border: wantsDebitCard === false ? '2px solid #5EFCE8' : '2px solid rgba(255,255,255,0.15)',
                    background: wantsDebitCard === false ? 'rgba(94, 252, 232, 0.12)' : 'rgba(255,255,255,0.04)',
                    color: wantsDebitCard === false ? '#5EFCE8' : 'rgba(255,255,255,0.7)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '10px',
                    fontSize: '16px',
                    fontWeight: '600'
                  }}
                >
                  <X size={28} />
                  {t('no') || 'No'}
                  <span style={{ fontSize: '12px', fontWeight: '400', opacity: 0.7 }}>{t('skipDebitCard') || 'Skip'}</span>
                </button>
              </div>

              {/* Animated Debit Card Preview - slides in when Yes is selected */}
              {wantsDebitCard === true && (
                <div style={{
                  marginTop: '32px',
                  display: 'flex',
                  justifyContent: 'center',
                  animation: 'slideInRight 0.5s ease-out'
                }}>
                  <div data-testid="debit-card-preview" style={{
                    width: '380px',
                    maxWidth: '100%',
                    height: '220px',
                    borderRadius: '20px',
                    background: 'linear-gradient(135deg, #0a2540 0%, #1a3a5c 40%, #0d4f6b 70%, #0a2540 100%)',
                    padding: '28px',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 40px rgba(94, 252, 232, 0.1)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}>
                    {/* Card shine effect */}
                    <div style={{
                      position: 'absolute',
                      top: '-50%',
                      left: '-50%',
                      width: '200%',
                      height: '200%',
                      background: 'radial-gradient(circle at 30% 30%, rgba(94, 252, 232, 0.08) 0%, transparent 50%)',
                      pointerEvents: 'none'
                    }} />
                    
                    {/* Top row: Bank name + Chip */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
                      <span style={{ color: '#5EFCE8', fontSize: '14px', fontWeight: '700', letterSpacing: '3px', textTransform: 'uppercase' }}>UAE BANK</span>
                      <div style={{ 
                        width: '45px', height: '34px', borderRadius: '6px',
                        background: 'linear-gradient(135deg, #d4af37 0%, #f5d76e 50%, #d4af37 100%)',
                        boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.3)'
                      }} />
                    </div>

                    {/* Card number */}
                    <div style={{ position: 'relative', zIndex: 1 }}>
                      <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '20px', letterSpacing: '4px', fontFamily: 'monospace', fontWeight: '500' }}>
                        **** **** **** 0000
                      </span>
                    </div>

                    {/* Bottom row: Name + Network */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', position: 'relative', zIndex: 1 }}>
                      <div>
                        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '4px' }}>Card Holder</span>
                        <span style={{ color: '#fff', fontSize: '15px', fontWeight: '600', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
                          {formData.firstName || 'FIRST'} {formData.lastName || 'LAST'}
                        </span>
                      </div>
                      <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '22px', fontWeight: '800', fontStyle: 'italic' }}>VISA</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 7/8: Review & Confirm */}
          {((step === 7 && !isFamilyVisa && !isFreelance) || (step === 8 && isFreelance)) && (
            <div className="step-form review-step" data-testid="step-7">
              <AnimatedStepIcon stepType="review" />
              <h3 className="step-title">{t('reviewConfirm') || 'Review & Confirm'}</h3>
              <p className="step-instruction">{t('reviewInstruction') || 'Please review your details before confirming.'}</p>
              
              <div className="review-sections">
                <div className="review-section">
                  <h4>{t('contactInfo') || 'Contact Information'}</h4>
                  <div className="review-field">
                    <label>{t('firstName') || 'First Name'}:</label>
                    <span>{formData.firstName}</span>
                  </div>
                  <div className="review-field">
                    <label>{t('lastName') || 'Last Name'}:</label>
                    <span>{formData.lastName}</span>
                  </div>
                  <div className="review-field">
                    <label>{t('dateOfPlanning') || 'Date of Planning'}:</label>
                    <span>{formData.dateOfPlanning}</span>
                  </div>
                  {ocrData.nationality && (
                    <div className="review-field">
                      <label>{t('nationality') || 'Nationality'}:</label>
                      <span>{ocrData.nationality}</span>
                    </div>
                  )}
                  <div className="review-field">
                    <label>{t('phoneNumber') || 'Phone'}:</label>
                    <span>{formData.phone}</span>
                  </div>
                  {!formData.sameAsPhone && formData.telegram && (
                    <div className="review-field">
                      <label>{t('telegramOrWhatsapp') || 'Telegram / WhatsApp'}:</label>
                      <span style={{ color: '#229ED9' }}>{formData.telegram}</span>
                    </div>
                  )}
                  <div className="review-field">
                    <label>{t('email') || 'Email'}:</label>
                    <span>{formData.email}</span>
                  </div>
                </div>
                
                <div className="review-section">
                  <h4>{t('uploadedDocs') || 'Uploaded Documents'}</h4>
                  <div className="document-thumbnails">
                    {passportPreview && <div className="doc-thumb"><img src={passportPreview} alt="Passport" /><span>{t('passport') || 'Passport'}</span></div>}
                    {photoPreview && <div className="doc-thumb"><img src={photoPreview} alt="Photo" /><span>{t('photo') || 'Photo'}</span></div>}
                  </div>
                  {ocrDone && (ocrData.passport_number || ocrData.date_of_birth || ocrData.nationality) && (
                    <div style={{ marginTop: '10px', fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
                      {ocrData.passport_number && <div>{t('passportNumber') || 'Passport Number'}: <span style={{ color: 'var(--text-primary)' }}>{ocrData.passport_number}</span></div>}
                      {ocrData.date_of_birth && <div>{t('dateOfBirth') || 'Date of Birth'}: <span style={{ color: 'var(--text-primary)' }}>{ocrData.date_of_birth}</span></div>}
                      {ocrData.nationality && <div>{t('nationality') || 'Nationality'}: <span style={{ color: 'var(--text-primary)' }}>{ocrData.nationality}</span></div>}
                      {ocrData.issue_date && <div>{t('issueDate') || 'Issue Date'}: <span style={{ color: 'var(--text-primary)' }}>{ocrData.issue_date}</span></div>}
                      {ocrData.expiry_date && <div>{t('expiryDate') || 'Expiry Date'}: <span style={{ color: 'var(--text-primary)' }}>{ocrData.expiry_date}</span></div>}
                    </div>
                  )}
                </div>
                
                <div className="review-section service-section">
                  <h4>{t('service') || 'Service'}</h4>
                  <div className="service-summary">
                    <span className="service-name">{getDisplayServiceName()}</span>
                    <span className="service-price">{getDisplayPrice()}</span>
                  </div>
                  {isVisaChange && transportMethod && visaDuration && (
                    <div style={{ marginTop: '8px', fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
                      <div>Visa {visaDuration} Days: AED {visaDuration === '30' ? '500' : '800'}</div>
                      <div>Transport ({transportMethod}): +AED {TRANSPORT_PRICES[transportMethod]}</div>
                    </div>
                  )}
                  {isFreelance && wantsDebitCard && (
                    <div style={{ marginTop: '8px', fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <CreditCard size={14} style={{ color: '#5EFCE8' }} />
                        <span>{t('debitCard') || 'Debit Card'}: +AED 500</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Action Buttons */}
        <div className="step-actions">
          {step > firstStep && (
            <button className="btn-secondary btn-back" onClick={handleBack} disabled={uploading} data-testid="back-btn">
              <ChevronLeft size={20} /> {t('back') || 'Back'}
            </button>
          )}
          {step < lastStep ? (
            <button className="btn-primary btn-next" onClick={handleNext} data-testid="next-btn"
              disabled={
                (step === -1 && !visaDuration) || 
                (step === 0 && isDubaiVisa && !visaDuration) || 
                (step === 0 && isVisaChange && !transportMethod) ||
                (step === 0 && isFamilyVisa && !familyMember) ||
                (step === 7 && isFreelance && wantsDebitCard === null)
              }>
              {t('next') || 'Next'} <ChevronRight size={20} />
            </button>
          ) : (
            <button className="btn-primary btn-confirm" onClick={handleSubmit} disabled={uploading} data-testid="confirm-btn">
              {uploading ? (t('submitting') || 'Submitting...') : (t('confirmBooking') || 'Confirm Booking')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default StepByStepBookingModal;
